import * as satellite from "satellite.js";

const radTodeg = 180 / Math.PI; // [rad] -> [deg]

interface GroundStationPosition {
  latitude: number;
  longitude: number;
  height: number;
}

// 패스 변수들 타입 정의
interface Pass {
  satelliteName: string;
  StartTime: Date;
  MaxElevation: number;
  EndTime: Date;
  AvailableTime: number;
}

export default class Orbit {
  private tle: string[];
  private satrec: any;

  // TLE 2줄로 입력하여 계산 => satellite.js 계산 시 한 줄씩 나눠서 입력
  constructor(tle: string) {
    const tleLines = tle.trim().split("\n");
    if (tleLines.length < 2) {
      throw new Error("Invalid TLE data");
    }
    this.tle = tleLines;
    this.satrec = satellite.twoline2satrec(tleLines[0], tleLines[1]);
  }

  // TLE로 부터 satellite number 반환
  get satnum() {
    return this.satrec.satnum;
  }

  // 계산 중 에러 추출
  get error() {
    return this.satrec.error;
  }

  // ECI 좌표 계산
  positionECI(time: Date) {
    const result = satellite.propagate(this.satrec, time);

    // 만약 결과가 boolean이면 에러를 throw
    if (typeof result === "boolean") {
      throw new Error("위성 위치를 계산할 수 없습니다.");
    }

    // result가 EciVec3<number> 타입이라고 가정하고 사용
    return result.position as satellite.EciVec3<number>;
  }

  // ECEF 좌표 계산
  positionECF(time: Date) {
    const positionEci = this.positionECI(time);
    const gmst = satellite.gstime(time);
    const positionEcf = satellite.eciToEcf(positionEci, gmst);
    return positionEcf;
  }

  // 위도(degree), 경도(degree), 고도(km) 좌표계로 계산
  positionGeodetic(timestamp: Date) {
    const positionEci = this.positionECI(timestamp);
    // 시간을 단위 변환 [Date] -> [GMST] : 그리니치 평균 항성시
    const gmst = satellite.gstime(timestamp);
    const positionGd = satellite.eciToGeodetic(positionEci, gmst);
    return {
      longitude: satellite.degreesLong(positionGd.longitude), // [degrees]
      latitude: satellite.degreesLat(positionGd.latitude), // [degrees]
      height: positionGd.height, // [km]
    };
  }

  // 스케줄 계산
computePassesElevation(
  satName: string,
  groundStationPosition: GroundStationPosition,
  startDate: Date | string,
  endDate: Date | string,
  minElevation: number = 0
) {
  // 1) 지상국 좌표(라디안)
  const gs = {
    latitude: satellite.degreesToRadians(groundStationPosition.latitude),
    longitude: satellite.degreesToRadians(groundStationPosition.longitude),
    height: groundStationPosition.height,
  };

  // 2) 입력을 UTC Date로 통일
  //    - 문자열이면 ISO8601(권장, "Z" 포함)로 넘어온다고 가정
  //    - 로컬 문자열을 넣고 싶다면, 호출부에서 dayjs(start).utc().toDate()로 넘기세요.
  const startUTC = new Date(startDate);
  const endUTC   = new Date(endDate);

  // 안정적인 시간 비교/증분을 위해 ms 단위로 루프
  let cur = new Date(startUTC.getTime());

  const passes: Pass[] = [];
  let pass: Pass | null = null;
  let maxElevation = 0;

  // 3) 루프/증분은 "UTC 전용"으로 (로컬 setSeconds 금지)
  while (cur.getTime() < endUTC.getTime()) {
    // 위치/각도 계산은 UTC Date를 그대로 사용
    const positionEcf = this.positionECF(cur);
    const look = satellite.ecfToLookAngles(gs, positionEcf);
    const elevation = look.elevation * radTodeg;

    if (elevation > minElevation) {
      if (!pass) {
        pass = {
          satelliteName: satName,
          StartTime: new Date(cur.getTime()), // UTC 시각 복제
          MaxElevation: elevation,
          EndTime: new Date(cur.getTime()),   // 초기화
          AvailableTime: 0,
        };
        maxElevation = elevation;
      } else {
        if (elevation > maxElevation) {
          maxElevation = elevation;
          pass.MaxElevation = maxElevation;
        }
        // 진행 중인 패스의 최신 EndTime도 함께 갱신(연속 구간 보장)
        pass.EndTime = new Date(cur.getTime());
      }
    } else if (pass) {
      // 패스 종료 시점: AvailableTime(초) 계산
      pass.EndTime = new Date(cur.getTime());
      pass.AvailableTime = (pass.EndTime.getTime() - pass.StartTime.getTime()) / 1000;
      passes.push(pass);
      pass = null;
    }

    // UTC 기준으로 정확히 +1초
    cur.setUTCSeconds(cur.getUTCSeconds() + 1);
    // (또는) cur = new Date(cur.getTime() + 1000);
  }

  // 4) 로그는 toISOString()으로 UTC 보장
  console.log(
    "Computed Passes List (UTC):",
    passes.map(p => ({
      satellite: p.satelliteName,
      StartTimeUTC: p.StartTime.toISOString(),
      EndTimeUTC: p.EndTime.toISOString(),
      MaxEl: p.MaxElevation,
      AvailSec: p.AvailableTime,
    }))
  );

  return passes;
}

}
